/**
 * Pure rendering helpers for the map editor SVG board. Kept separate from
 * MapEditor.vue so the board geometry and terrain-icon math are testable and
 * reusable, and the component file stays focused on interaction state.
 *
 * Board constants MUST match BattleplanMap.vue (the public preview render).
 */
import {
  faTree,
  faWandSparkles,
  faHouseChimneyCrack,
  faStar,
  faCircle,
  faBiohazard,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { TerrainType, TerrainSize, ObjectiveType } from '@scrolldex/shared';

export const SCALE = 8;
export const W = 60 * SCALE; // 480 — 60" board width
export const H = 44 * SCALE; // 352 — 44" board height
export const OBJ_RADIUS = (6 + 40 / 25.4) / 2; // 6" + 40mm diameter → ≈ 3.787" radius

/** Inches → scaled SVG units. */
export function s(inches: number): number {
  return inches * SCALE;
}

/** Terrain footprint radius (scaled units) by size. */
export function terrainRadius(size: TerrainSize): number {
  switch (size) {
    case TerrainSize.Small:
      return s(2.5);
    case TerrainSize.Medium:
      return s(3.5); // 7" diameter
    case TerrainSize.Large:
      return s(OBJ_RADIUS * 1.5);
  }
}

export function terrainIconDef(type: TerrainType): IconDefinition {
  switch (type) {
    case TerrainType.Obscuring:
      return faTree;
    case TerrainType.PlaceOfPower:
      return faWandSparkles;
    case TerrainType.Ruin:
    default:
      return faHouseChimneyCrack;
  }
}

/** SVG transform that centers a Font Awesome glyph at (cx, cy) scaled to `size`. */
export function terrainIconTransform(
  type: TerrainType,
  cx: number,
  cy: number,
  size: number,
): string {
  const def = terrainIconDef(type);
  const [w, h] = def.icon as [number, number, string[], string, string | string[]];
  const scale = size / Math.max(w, h);
  const tx = cx - (w * scale) / 2;
  const ty = cy - (h * scale) / 2;
  return `translate(${tx},${ty}) scale(${scale})`;
}

export function terrainIconPath(type: TerrainType): string {
  const def = terrainIconDef(type);
  const pathData = def.icon[4];
  return Array.isArray(pathData) ? (pathData as string[]).join(' ') : (pathData as string);
}

/**
 * Font Awesome icon for objectives drawn as a glyph instead of a letter, or
 * null to fall back to the lettered label (objectiveDisplayLabel).
 */
export function objectiveIconDef(type: ObjectiveType): IconDefinition | null {
  switch (type) {
    case ObjectiveType.Regular:
      return faStar;
    default:
      return null;
  }
}

/** SVG transform centering the objective glyph at (cx, cy) scaled to `size`. */
export function objectiveIconTransform(
  type: ObjectiveType,
  cx: number,
  cy: number,
  size: number,
): string {
  const def = objectiveIconDef(type);
  if (!def) return '';
  const [w, h] = def.icon as [number, number, string[], string, string | string[]];
  const scale = size / Math.max(w, h);
  const tx = cx - (w * scale) / 2;
  const ty = cy - (h * scale) / 2;
  return `translate(${tx},${ty}) scale(${scale})`;
}

export function objectiveIconPath(type: ObjectiveType): string {
  const def = objectiveIconDef(type);
  if (!def) return '';
  const pathData = def.icon[4];
  return Array.isArray(pathData) ? (pathData as string[]).join(' ') : (pathData as string);
}

// ─── Freeform map icons ───────────────────────────────────────────────────────
// Icons are placed by a freeform Font Awesome name (IMapIcon.icon). This registry
// is the set we can render today; unknown names fall back to a solid circle so the
// data stays forward-compatible as we add more. To support a new icon, import its
// definition and add it here (and register it in main.ts for the picker swatch).
export const DEFAULT_ICON_SIZE = 28;

const MAP_ICON_DEFS: Record<string, IconDefinition> = {
  'house-chimney-crack': faHouseChimneyCrack,
  tree: faTree,
  'wand-sparkles': faWandSparkles,
  circle: faCircle,
  biohazard: faBiohazard,
};

export function mapIconDef(name: string): IconDefinition {
  return MAP_ICON_DEFS[name] ?? faCircle;
}

/** SVG transform centering a freeform icon glyph at (cx, cy) scaled to `size`. */
export function mapIconTransform(name: string, cx: number, cy: number, size: number): string {
  const def = mapIconDef(name);
  const [w, h] = def.icon as [number, number, string[], string, string | string[]];
  const scale = size / Math.max(w, h);
  const tx = cx - (w * scale) / 2;
  const ty = cy - (h * scale) / 2;
  return `translate(${tx},${ty}) scale(${scale})`;
}

export function mapIconPath(name: string): string {
  const pathData = mapIconDef(name).icon[4];
  return Array.isArray(pathData) ? (pathData as string[]).join(' ') : (pathData as string);
}
